import { Concepts } from "lib/data/Concepts";
import Wedges from "../components/Wedge";
import { RelatedFrameworks, Image, H1, H2, P, Updated } from "design_lib";

export default function Differentiation() {
  return (
    <div>
      Differentiation | Features | Convenience | Brand | Quality | Product Quality | Customer
      Service | Jobs to be Done | Consumption Chain | Strategy Canvas | Buyer Utility Map
      <H1>Definition/Description</H1>
      <P>
        Differentiation is the way a company separates itself from the rest of the competition. It
        is one of the fundamental and foundational building blocks in strategy to build brand
        recognition, increase market share, and to grow as a company. It does this by raising a
        customer’s willingness to pay through comparative improvements in (i) the product and (ii)
        the customer interaction. The value stick demonstrates this concept:
      </P>
      <div className="d-flex flex-wrap gap-3 justify-content-between">
        <Wedges />
      </div>
      <P>
        There are two sub categories of differentiation: product and customer. See the graphic below
        for examples of improvements to each differentiation subcategory:
      </P>
      <Image
        src={require("lib/img/concepts/Differentiation_Venn_Diagram.png")}
        alt="venn diagram"
      />
      <P>
        The product sub-category focuses on differentiating through certain aspects of a product
        while the customer sub-category focuses on differentiating through certain aspects that
        interact with a customer.
      </P>
      <P>*Insert Jeep Video Graphic right here*</P>
      <H1>Examples</H1>
      <H2>Apple</H2>
      <P>
        Apple is known for its innovation and high-quality products. It differentiates itself from
        its competitors in many aspects; its marketing, branding, and products.: all evoke high
        quality perceptions. It pays attention to details that other firms ignore such as packaging.
        High quality packaging leads to a unique “unboxing” experience. All of this leads to product
        differentiation.
      </P>
      <H2>Fender</H2>
      <P>
        Fender- Fender Stratocaster guitars are known for their three-coil pickup system for diverse
        tone possibility from clean to heavy sounds. Additionally, Stratocasters are heavily
        customizable for whichever sound quality someone may want. On the other hand, other guitar
        companies such as Gibson primarily utilize humbucker pickups that capture deep and heavy
        tones and are not so easily customized. Fender’s niche strategy for customizable tones is
        focused differentiation.
      </P>
      <H2>Chick-Fil-A</H2>
      <P>
        One of the many ways Chick-Fil-A differentiates itself is through superior customer service.
        They always seek to delight the customers by politely serving them. It is no surprise that
        they are consistently ranked high in the American Customer Satisfaction Index; in 2024 they
        were ranked 1st in the fast-food industry. Due to their quality customer service, they have
        loyal customers and been seeing consistent growth.
      </P>
      <H1>How it fits into Generic Strategies</H1>
      <Image
        src={require("lib/img/concepts/Differentiation_Porters_Generic_Strategies.png")}
        alt="Generic Strategies"
      />
      <P>
        As shown in the Generic Strategies framework *Hyperlink to codex article*, leaders choosing
        to leverage differentiation must choose whether to target the industry broadly or to target
        a narrower portion of the industry. Broad differentiation is to differentiate for many
        segments in industry while Focused Differentiation is provides differentiation within a
        specific segment of the market. For example, while American Airlines offers standard flights
        to virtually between any two locations in the United States at any time and focuses on
        multiple segments like family travel, business travel and everything in between. On the
        other hand, Southwest Airlines offers a focused travel experience that works to get people
        out of their car and onto a flight. In this case, Southwest operates in a more “niche”
        market than American Airlines in both the locations served and the people flown. This is
        manifest in the types of flights flown. For example, Southwest does not currently offer
        overnight “red-eye” flights. Over time, companies like Southwest may seek to shift between
        Focused Differentiation and Differentiation to capture greater overall market share.
      </P>
      <P>*Insert Video Here*</P>
      <H1>Application</H1>
      <P>
        Many frameworks have been created in order to assist Strategists in assessing how they are
        currently differentiating and how they can differentiate more. Some examples include the
        Strategy Canvas and the Buyer Utility Map (see Frameworks below for a more comprehensive
        list).
      </P>
      <H1>History</H1>
      <P>
        Although not given its official name until the modern era, Differentiation Strategy has been
        around since the creation of business and competition. Throughout history, businesses have
        tried to differentiate themselves from the competition by either having the best product
        quality or best customer interaction. One of the earliest records of a customer evaluating
        these factors of differentiation was filed to EA Nasir around 1750 B.C. In this cuneiform
        tablet, the customer complained about his rudeness during the sale of substandard copper.
        This goes to show that even in ancient times, customers looked for the best quality product
        or the best customer interaction and differentiation matters.
      </P>
      <H1>Sources</H1>
      <div>
        <P>
          MacMillan, I., & McGrath, R. (1997, July). “Discovering the New Points of
          Differentiation”. Harvard Business Review.
          https://hbr.org/1997/07/discovering-new-points-of-differentiation
        </P>
        <P>
          Dyer, J., Godfrey, P., Jensen, R., Bryce, D. (2024, February). “Strategic Management:
          Concepts and Cases, 5th Edition”, Chapter 5, pp 81-97. Wiley.
          https://www.wiley.com/en-us/Strategic+Management%3A+Concepts+and+Cases%2C+5th+Edition-p-9781394161881
        </P>
        <P>
          Institute for Manufacturing. (2016). “Porter's Generic Competitive Strategies (ways of
          competing)”. University of Cambridge.
          https://www.ifm.eng.cam.ac.uk/research/dstools/porters-generic-competitive-strategies/
        </P>
        <P>Porter, M. (1985). "Competitive Advantage", Ch. 1, pp 11-15. The Free Press.</P>
        <P>
          ACSI Staff. (2024, March). “Fast Food Restaurants”. American Customer Satisfaction Index.
          https://theacsi.org/industries/restaurant/fast-food-restaurants/
        </P>
        <P>Chick-Fil-A financial statements: Chick-Fil-A-2022.pdf</P>
        <P>
          Chick-fil-A, Inc. (2023, March). “Chick-fil-A 2022 Franchise Disclosure Document”.
          Franchise Times. https://www.franchisetimes.com/app/Chick-Fil-A-2022.pdf
        </P>
        <P>
          Southwest:
          https://www.investopedia.com/articles/investing/061015/how-southwest-different-other-airlines.asp
        </P>
      </div>
      <RelatedFrameworks
        concepts={[
          Concepts.JobsToBeDoneConcept,
          Concepts.ConsumptionChain,
          Concepts.StrategyCanvas,
          Concepts.PortersGenericStrategies,
        ]}
        unfinishedConcepts={["Buyer Utility Map"]}
      />
      <Updated date="2/21/25" />
    </div>
  );
}
